/**
 * Utilitários para detecção e gerenciamento de tipos de comissões
 * - CIPA (NR-05): Comissão Interna de Prevenção de Acidentes
 * - CIPAMIN (NR-22): Comissão Interna de Prevenção de Acidentes na Mineração
 * - CIPATR (NR-31): Comissão Interna de Prevenção de Acidentes no Trabalho Rural
 */

import { supabase } from '../config/supabase';
import { obterTipoComissaoPorCNAE } from '../services/cnpjService';

/**
 * Verifica se um CNAE pertence ao setor de mineração (NR-22)
 * DEPRECADO: Use obterTipoComissaoPorCNAE() do cnpjService.ts
 * @param cnae - Código CNAE da empresa
 * @param nomeEmpresa - Nome da empresa (opcional, usado como fallback)
 * @returns true se for empresa de mineração
 */
export const isMiningCompany = async (cnae: string | null | undefined, nomeEmpresa?: string | null): Promise<boolean> => {
  console.log('🔍 [isMiningCompany] INÍCIO - Verificando CNAE:', cnae, 'Nome:', nomeEmpresa);
  
  // Se tiver CNAE, consultar banco de dados
  if (cnae) {
    try {
      const tipoComissao = await obterTipoComissaoPorCNAE(cnae);
      const isMining = tipoComissao === 'CIPAMIN';
      console.log(`✅ [isMiningCompany] Tipo comissão do banco: ${tipoComissao} - É mineração? ${isMining}`);
      return isMining;
    } catch (error) {
      console.error('❌ [isMiningCompany] Erro ao consultar banco:', error);
    }
  }
  
  // Fallback: Se não tiver CNAE, verificar pelo nome da empresa
  if (nomeEmpresa) {
    const nomeUpper = nomeEmpresa.toUpperCase();
    const palavrasChave = [
      'MINERIO', 'MINÉRIO', 'MINERACAO', 'MINERAÇÃO', 'MINING', 
      'MINERAIS', 'MINERAL', 'FERRO', 'OURO', 'COBRE', 'NICKEL',
      'BAUXITA', 'MANGANES', 'MANGANÊS', 'CARVAO', 'CARVÃO',
      'EXTRAÇÃO', 'EXTRACAO', 'MINE', 'MINAS', 'MINERALOGIA'
    ];
    
    const contemPalavraChave = palavrasChave.some(palavra => nomeUpper.includes(palavra));
    
    if (contemPalavraChave) {
      console.log('✅ [isMiningCompany] Nome da empresa contém palavras-chave de mineração:', nomeEmpresa);
      return true;
    }
    
    console.log('❌ [isMiningCompany] Nome da empresa não indica mineração:', nomeEmpresa);
  }
  
  console.log('❌ [isMiningCompany] CNAE é null/undefined/empty e nome não indica mineração - retornando false');
  return false;
};

/**
 * Verifica se um CNAE pertence ao setor rural/agropecuário (NR-31)
 * DEPRECADO: Use obterTipoComissaoPorCNAE() do cnpjService.ts
 * @param cnae - Código CNAE da empresa
 * @param nomeEmpresa - Nome da empresa (opcional, usado como fallback)
 * @returns true se for empresa rural/agropecuária
 */
export const isRuralCompany = async (cnae: string | null | undefined, nomeEmpresa?: string | null): Promise<boolean> => {
  console.log('🔍 [isRuralCompany] INÍCIO - Verificando CNAE:', cnae, 'Nome:', nomeEmpresa);
  
  // Se tiver CNAE, consultar banco de dados
  if (cnae) {
    try {
      const tipoComissao = await obterTipoComissaoPorCNAE(cnae);
      const isRural = tipoComissao === 'CIPATR';
      console.log(`✅ [isRuralCompany] Tipo comissão do banco: ${tipoComissao} - É rural? ${isRural}`);
      return isRural;
    } catch (error) {
      console.error('❌ [isRuralCompany] Erro ao consultar banco:', error);
    }
  }
  
  // Fallback: Se não tiver CNAE, verificar pelo nome da empresa
  if (nomeEmpresa) {
    const nomeUpper = nomeEmpresa.toUpperCase();
    const palavrasChave = [
      'AGRICOLA', 'AGRÍCOLA', 'FAZENDA', 'SITIO', 'SÍTIO', 'CHACARA', 'CHÁCARA',
      'PECUARIA', 'PECUÁRIA', 'AGROPECUARIA', 'AGROPECUÁRIA', 'RURAL',
      'FLORESTAL', 'REFLORESTAMENTO', 'SILVICULTURA',
      'PESCA', 'PESQUEIRO', 'AQUICULTURA', 'PISCICULTURA',
      'LAVOURA', 'PLANTACAO', 'PLANTAÇÃO', 'CULTIVO', 'CRIACAO', 'CRIAÇÃO',
      'GADO', 'BOVINO', 'SUINO', 'SUÍNO', 'AVICOLA', 'AVÍCOLA', 'GRANJA',
      'HORTIFRUTI', 'HORTALICA', 'HORTALIÇA'
    ];
    
    const contemPalavraChave = palavrasChave.some(palavra => nomeUpper.includes(palavra));
    
    if (contemPalavraChave) {
      console.log('✅ [isRuralCompany] Nome da empresa contém palavras-chave rurais:', nomeEmpresa);
      return true;
    }
    
    console.log('❌ [isRuralCompany] Nome da empresa não indica atividade rural:', nomeEmpresa);
  }
  
  console.log('❌ [isRuralCompany] CNAE é null/undefined/empty e nome não indica atividade rural - retornando false');
  return false;
};

/**
 * Retorna o tipo de comissão baseado no CNAE
 * DEPRECADO: Use obterTipoComissaoPorCNAE() do cnpjService.ts
 * @param cnae - Código CNAE da empresa
 * @param nomeEmpresa - Nome da empresa (opcional, usado como fallback)
 * @returns 'CIPAMIN' para mineração, 'CIPATR' para rural, 'CIPA' para demais
 */
export const getTipoComissao = async (cnae: string | null | undefined, nomeEmpresa?: string | null): Promise<'CIPA' | 'CIPAMIN' | 'CIPATR'> => {
  console.log('🔍 [getTipoComissao] INÍCIO - Verificando CNAE:', cnae, 'Nome:', nomeEmpresa);
  
  // Se tiver CNAE, consultar banco de dados diretamente
  if (cnae) {
    try {
      const tipoComissao = await obterTipoComissaoPorCNAE(cnae);
      console.log(`✅ [getTipoComissao] RESULTADO do banco: ${tipoComissao}`);
      return tipoComissao;
    } catch (error) {
      console.error('❌ [getTipoComissao] Erro ao consultar banco:', error);
    }
  }
  
  // Prioridade 1: Verificar se é mineração (NR-22) - fallback por nome
  const isMining = await isMiningCompany(cnae, nomeEmpresa);
  if (isMining) {
    console.log(`✅ [getTipoComissao] RESULTADO: CIPAMIN (isMining: true)`);
    return 'CIPAMIN';
  }
  
  // Prioridade 2: Verificar se é rural/agropecuário (NR-31) - fallback por nome
  const isRural = await isRuralCompany(cnae, nomeEmpresa);
  if (isRural) {
    console.log(`✅ [getTipoComissao] RESULTADO: CIPATR (isRural: true)`);
    return 'CIPATR';
  }
  
  // Padrão: CIPA (NR-05)
  console.log(`✅ [getTipoComissao] RESULTADO: CIPA (padrão)`);
  return 'CIPA';
};

/**
 * Retorna informações visuais para o tipo de comissão
 * @param cnae - Código CNAE da empresa
 * @param tipoComissaoBanco - Tipo de comissão do banco (prioridade)
 * @param nomeEmpresa - Nome da empresa (opcional, usado como fallback)
 */
export const getComissaoVisualInfo = async (
  cnae: string | null | undefined, 
  tipoComissaoBanco?: string | null,
  nomeEmpresa?: string | null
) => {
  console.log('🔍 [getComissaoVisualInfo] INÍCIO - CNAE:', cnae, 'tipo_comissao do banco:', tipoComissaoBanco, 'nome:', nomeEmpresa);
  
  // PRIORIDADE 1: usar tipo_comissao do banco se existir
  let tipoComissao: 'CIPA' | 'CIPAMIN' | 'CIPATR' = 'CIPA';
  
  // 🚨 ATENÇÃO: Verificar se o tipo_comissao do banco está correto!
  // Se o CNAE indica um tipo diferente, usar a detecção pelo CNAE
  const tipoDetectadoPeloCNAE = await getTipoComissao(cnae, nomeEmpresa);
  console.log('🔍 [getComissaoVisualInfo] Tipo detectado pelo CNAE:', tipoDetectadoPeloCNAE);
  console.log('🔍 [getComissaoVisualInfo] Tipo do banco:', tipoComissaoBanco);
  
  if (tipoComissaoBanco === 'CIPAMIN') {
    tipoComissao = 'CIPAMIN';
    console.log('✅ [getComissaoVisualInfo] Usando tipo_comissao do banco: CIPAMIN');
  } else if (tipoComissaoBanco === 'CIPATR') {
    tipoComissao = 'CIPATR';
    console.log('✅ [getComissaoVisualInfo] Usando tipo_comissao do banco: CIPATR');
  } else if (tipoComissaoBanco === 'CIPA') {
    // ⚠️ VERIFICAR: Se o banco diz CIPA mas CNAE indica outro tipo
    if (tipoDetectadoPeloCNAE !== 'CIPA') {
      console.warn('⚠️ [getComissaoVisualInfo] CONFLITO! Banco diz CIPA mas CNAE indica:', tipoDetectadoPeloCNAE);
      console.warn('⚠️ [getComissaoVisualInfo] Usando detecção pelo CNAE (mais confiável)');
      tipoComissao = tipoDetectadoPeloCNAE;
    } else {
      tipoComissao = 'CIPA';
      console.log('✅ [getComissaoVisualInfo] Usando tipo_comissao do banco: CIPA');
    }
  } else {
    // PRIORIDADE 2: detectar pelo CNAE ou nome
    tipoComissao = tipoDetectadoPeloCNAE;
    console.log('✅ [getComissaoVisualInfo] Detectado pelo CNAE/nome:', tipoComissao);
  }
  
  // Retornar informações baseadas no tipo
  if (tipoComissao === 'CIPAMIN') {
    const info = {
      tipo: 'CIPAMIN' as const,
      cor: 'orange',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
      borderColor: 'border-orange-500',
      norma: 'NR-22',
      descricao: 'Comissão Interna de Prevenção de Acidentes na Mineração'
    };
    console.log('✅ [getComissaoVisualInfo] RESULTADO FINAL:', info);
    return info;
  }
  
  if (tipoComissao === 'CIPATR') {
    const info = {
      tipo: 'CIPATR' as const,
      cor: 'green',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-500',
      norma: 'NR-31',
      descricao: 'Comissão Interna de Prevenção de Acidentes no Trabalho Rural'
    };
    console.log('✅ [getComissaoVisualInfo] RESULTADO FINAL:', info);
    return info;
  }
  
  // Padrão: CIPA
  const info = {
    tipo: 'CIPA' as const,
    cor: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-500',
    norma: 'NR-05',
    descricao: 'Comissão Interna de Prevenção de Acidentes'
  };
  console.log('✅ [getComissaoVisualInfo] RESULTADO FINAL:', info);
  return info;
};

/**
 * Retorna os riscos específicos baseados no tipo de comissão
 */
export const getRiscosEspecificos = async (cnae: string | null | undefined) => {
  console.log('🔍 [getRiscosEspecificos] INÍCIO - Verificando CNAE:', cnae);
  const tipoComissao = await getTipoComissao(cnae);
  console.log('🔍 [getRiscosEspecificos] tipoComissao:', tipoComissao);
  
  if (tipoComissao === 'CIPAMIN') {
    const riscos = [
      { 
        nome: 'Soterramento', 
        nivel: 'alto', 
        descricao: 'Risco de soterramento em galerias e minas',
        cor: 'red'
      },
      { 
        nome: 'Explosivos', 
        nivel: 'alto', 
        descricao: 'Uso de dinamite e detonações',
        cor: 'red'
      },
      { 
        nome: 'Gases', 
        nivel: 'alto', 
        descricao: 'Metano, monóxido de carbono e outros gases',
        cor: 'red'
      },
      { 
        nome: 'Desmoronamento', 
        nivel: 'alto', 
        descricao: 'Queda de estruturas e rochas',
        cor: 'red'
      },
      { 
        nome: 'Poeiras Minerais', 
        nivel: 'medio', 
        descricao: 'Silicose, pneumoconiose',
        cor: 'yellow'
      },
      { 
        nome: 'Ventilação', 
        nivel: 'medio', 
        descricao: 'Qualidade do ar em ambientes confinados',
        cor: 'yellow'
      }
    ];
    console.log('✅ [getRiscosEspecificos] RETORNANDO riscos de MINERAÇÃO:', riscos.length, 'itens');
    return riscos;
  }
  
  if (tipoComissao === 'CIPATR') {
    const riscos = [
      { 
        nome: 'Agrotóxicos', 
        nivel: 'alto', 
        descricao: 'Exposição a pesticidas e herbicidas',
        cor: 'red'
      },
      { 
        nome: 'Máquinas Agrícolas', 
        nivel: 'alto', 
        descricao: 'Tratores, colheitadeiras e implementos',
        cor: 'red'
      },
      { 
        nome: 'Animais Peçonhentos', 
        nivel: 'medio', 
        descricao: 'Serpentes, escorpiões, aranhas',
        cor: 'yellow'
      },
      { 
        nome: 'Trabalho em Altura', 
        nivel: 'alto', 
        descricao: 'Poda de árvores, colheita em altura',
        cor: 'red'
      },
      { 
        nome: 'Zoonoses', 
        nivel: 'medio', 
        descricao: 'Leptospirose, tétano, raiva',
        cor: 'yellow'
      },
      { 
        nome: 'Exposição Solar', 
        nivel: 'medio', 
        descricao: 'Radiação UV, insolação, desidratação',
        cor: 'yellow'
      },
      { 
        nome: 'Ferramentas Cortantes', 
        nivel: 'medio', 
        descricao: 'Facões, foices, motosserras',
        cor: 'yellow'
      }
    ];
    console.log('✅ [getRiscosEspecificos] RETORNANDO riscos RURAIS:', riscos.length, 'itens');
    return riscos;
  }
  
  // Padrão: CIPA
  const riscos = [
    { 
      nome: 'Quedas', 
      nivel: 'medio', 
      descricao: 'Quedas de mesmo nível e altura',
      cor: 'yellow'
    },
    { 
      nome: 'Ergonômicos', 
      nivel: 'baixo', 
      descricao: 'Postura, movimentos repetitivos',
      cor: 'green'
    },
    { 
      nome: 'Químicos', 
      nivel: 'medio', 
      descricao: 'Exposição a produtos químicos',
      cor: 'yellow'
    },
    { 
      nome: 'Máquinas', 
      nivel: 'medio', 
      descricao: 'Operação de equipamentos',
      cor: 'yellow'
    }
  ];
  console.log('✅ [getRiscosEspecificos] RETORNANDO riscos GERAIS:', riscos.length, 'itens');
  return riscos;
};

/**
 * Atualiza o tipo de comissão da empresa no banco de dados
 */
export const atualizarTipoComissao = async (empresaId: string, cnae: string | null | undefined) => {
  console.log('🔍 [atualizarTipoComissao] INÍCIO');
  console.log('🔍 [atualizarTipoComissao] empresaId:', empresaId);
  console.log('🔍 [atualizarTipoComissao] cnae:', cnae);
  
  const tipoComissao = await getTipoComissao(cnae);
  console.log('🔍 [atualizarTipoComissao] tipoComissao calculado:', tipoComissao);
  
  try {
    console.log('💾 [atualizarTipoComissao] Atualizando banco de dados...');
    const { error } = await supabase
      .from('cp_empresas')
      .update({ tipo_comissao: tipoComissao })
      .eq('id', empresaId);
    
    if (error) throw error;
    
    console.log(`✅ [atualizarTipoComissao] Tipo de comissão atualizado para: ${tipoComissao}`);
    return { success: true, tipoComissao };
  } catch (error) {
    console.error('❌ [atualizarTipoComissao] Erro ao atualizar tipo de comissão:', error);
    return { success: false, error };
  }
};

/**
 * Retorna o nome da SIPAT baseado no tipo de comissão
 */
export const getSipatName = async (cnae: string | null | undefined): Promise<string> => {
  const tipoComissao = await getTipoComissao(cnae);
  if (tipoComissao === 'CIPAMIN') return 'SIPATMIN';
  if (tipoComissao === 'CIPATR') return 'SIPATR';
  return 'SIPAT';
};

/**
 * Retorna informações de treinamento baseado no tipo de comissão
 */
export const getTreinamentoInfo = async (cnae: string | null | undefined) => {
  console.log('🔍 [getTreinamentoInfo] INÍCIO - Verificando CNAE:', cnae);
  const tipoComissao = await getTipoComissao(cnae);
  console.log('🔍 [getTreinamentoInfo] tipoComissao:', tipoComissao);
  
  if (tipoComissao === 'CIPAMIN') {
    const info = {
      cargaHoraria: 20,
      nome: 'Treinamento CIPAMIN',
      conteudo: [
        'Legislação de Mineração (NR-22)',
        'Riscos da Mineração (soterramento, explosivos, gases)',
        'Prevenção de Acidentes na Mineração',
        'Procedimentos de Emergência',
        'Inspeções de Segurança em Minas',
        'Equipamentos de Proteção Individual/Coletiva'
      ]
    };
    console.log('✅ [getTreinamentoInfo] RESULTADO:', {
      cargaHoraria: info.cargaHoraria,
      nome: info.nome,
      conteudo: info.conteudo.length + ' itens'
    });
    return info;
  }
  
  if (tipoComissao === 'CIPATR') {
    const info = {
      cargaHoraria: 20,
      nome: 'Treinamento CIPATR',
      conteudo: [
        'Legislação Rural (NR-31)',
        'Riscos no Trabalho Rural (agrotóxicos, máquinas agrícolas, animais)',
        'Prevenção de Acidentes no Campo',
        'Uso Seguro de Agrotóxicos e EPIs',
        'Segurança em Máquinas e Implementos Agrícolas',
        'Primeiros Socorros em Ambiente Rural',
        'Prevenção de Zoonoses e Doenças Ocupacionais Rurais'
      ]
    };
    console.log('✅ [getTreinamentoInfo] RESULTADO:', {
      cargaHoraria: info.cargaHoraria,
      nome: info.nome,
      conteudo: info.conteudo.length + ' itens'
    });
    return info;
  }
  
  // Padrão: CIPA
  const info = {
    cargaHoraria: 20,
    nome: 'Treinamento CIPA',
    conteudo: [
      'Legislação de Segurança do Trabalho (NR-05)',
      'Identificação de Riscos',
      'Prevenção de Acidentes',
      'Investigação de Acidentes',
      'Primeiros Socorros',
      'Ergonomia'
    ]
  };
  console.log('✅ [getTreinamentoInfo] RESULTADO:', {
    cargaHoraria: info.cargaHoraria,
    nome: info.nome,
    conteudo: info.conteudo.length + ' itens'
  });
  return info;
};

/**
 * Retorna o grupo de mineração baseado no CNAE
 */
export const getGrupoMineracao = (cnae: string | null | undefined): string | null => {
  if (!cnae) return null;
  
  const cnaeNumerico = cnae.replace(/[^\d]/g, '');
  
  if (cnaeNumerico.startsWith('07')) return 'Minérios Metálicos';
  if (cnaeNumerico.startsWith('08')) return 'Minerais Não-Metálicos';
  if (cnaeNumerico.startsWith('09')) return 'Apoio à Extração';
  
  return null;
};

/**
 * Verifica se empresa tem CNAEs de mineração no banco
 */
export const verificarCNAEMineracao = async (cnae: string): Promise<boolean> => {
  try {
    const cnaeNumerico = cnae.replace(/[^\d]/g, '');
    
    const { data, error } = await supabase
      .from('cp_cnaes_mineracao')
      .select('id')
      .or(`cnae.eq.${cnae},cnae.eq.${cnaeNumerico},cnae.like.${cnaeNumerico.substring(0, 2)}%`)
      .limit(1);
    
    if (error) {
      console.error('Erro ao verificar CNAE mineração:', error);
      return false;
    }
    
    return !!(data && data.length > 0);
  } catch (error) {
    console.error('Erro ao verificar CNAE mineração:', error);
    return false;
  }
};
